import jobManagerApi from "../Services/job-manager-api";
import type { MusicJobStatusPayload } from "../types/MusicJobStatus";
import { computeTimeTakenInSec } from "../types/MusicJobStatus";
import { CategoryName, JobStatus, JobTitle } from "./constant";

export interface BuildMusicJobPayloadOptions {
  processInitiatedOn?: string;
  timeTakenInSec?: number;
  jobMetaData?: Record<string, unknown>;
}

export const buildMusicJobPayload = (
  jobID: string,
  tag: string,
  status: number,
  options: BuildMusicJobPayloadOptions = {},
): MusicJobStatusPayload => ({
  jobID,
  jobTitle: JobTitle.MUSIC_GENERATOR,
  description: CategoryName.MUSIC_GENERATOR_AI,
  status,
  tags: tag,
  ...(options.processInitiatedOn
    ? { ProcessInitiatedOn: options.processInitiatedOn }
    : {}),
  ...(options.timeTakenInSec !== undefined
    ? { TimeTakenInSec: options.timeTakenInSec }
    : {}),
  jobMetaData: {
    additionalProp1: [tag],
    ...(options.processInitiatedOn
      ? { ProcessInitiatedOn: options.processInitiatedOn }
      : {}),
    ...(options.timeTakenInSec !== undefined
      ? { TimeTakenInSec: options.timeTakenInSec }
      : {}),
    ...options.jobMetaData,
  },
});

export const postMusicJobStatus = async (
  jobID: string,
  tag: string,
  status: (typeof JobStatus)[keyof typeof JobStatus],
  options: BuildMusicJobPayloadOptions = {},
): Promise<void> => {
  try {
    await jobManagerApi.postJobStatus(
      buildMusicJobPayload(jobID, tag, status, options),
    );
  } catch {
    // Job status posting may fail with CORS when running locally
  }
};

export interface EnqueuedMusicJob {
  jobID: string;
  tag: string;
  processInitiatedOn: string;
}

/**
 * Register a music job as ENQUEUED (status 10) before doc-editor submission.
 * Sends `ProcessInitiatedOn` to Job Manager so MusicGenerator can read it via GET.
 */
export const enqueueMusicJob = async (
  jobID: string,
  tag: string,
): Promise<EnqueuedMusicJob> => {
  const processInitiatedOn = new Date().toISOString();
  await postMusicJobStatus(jobID, tag, JobStatus.ENQUEUED, {
    processInitiatedOn,
  });
  return { jobID, tag, processInitiatedOn };
};

/** Post IN_PROGRESS (status 20). Intended for MusicGenerator worker on job pickup. */
export const postMusicJobInProgress = async (
  jobID: string,
  tag: string,
  processInitiatedOn: string,
): Promise<void> => {
  await postMusicJobStatus(jobID, tag, JobStatus.IN_PROGRESS, {
    processInitiatedOn,
  });
};

/**
 * Post SUCCESS (status 30) with `timeTakenInSec` from `processInitiatedOn`.
 * Intended for MusicGenerator worker on completion (Job Manager may also compute this).
 */
export const postMusicJobCompleted = async (
  jobID: string,
  tag: string,
  processInitiatedOn: string,
  jobMetaData?: Record<string, unknown>,
): Promise<void> => {
  await postMusicJobStatus(jobID, tag, JobStatus.SUCCESS, {
    processInitiatedOn,
    timeTakenInSec: computeTimeTakenInSec(processInitiatedOn),
    jobMetaData,
  });
};
